// frontend/src/lib/appLinks.js
//
// Single source of truth for native app store links and the lightweight
// device-detection helpers used by the iOS install-conversion layer.
//
// Scope (do not expand without explicit approval):
//   - APP_STORE_URL / APP_STORE_APP_ID:   live iOS listing
//   - PLAY_STORE_URL:                     reserved placeholder, NOT used yet
//   - isIPhoneWebSafari():                show "Get the iOS app" CTA only here
//   - isDesktopWeb():                     show soft "open on your phone" hint
//
// Both helpers are SSR-safe (`typeof window === 'undefined'` guard) and
// return false inside the Capacitor native shell so we never tell a user
// who is already in the iOS app to "Get the iOS app".

// ---- Constants ------------------------------------------------------------

export const APP_STORE_APP_ID = '6770409098';
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/open-runs-usa/id6770409098';

// Reserved for when the Android wrapper ships. Intentionally not consumed
// by any component today. Do not import this in UI code until Play Store
// is live and an explicit task approves it.
export const PLAY_STORE_URL = null;

// ---- Device detection -----------------------------------------------------

const isCapacitorNative = () => {
  if (typeof window === 'undefined') return false;
  // @capacitor/core sets window.Capacitor; isNativePlatform() is the
  // canonical check, but we fall back to the legacy `platform` field for
  // older shells.
  const cap = window.Capacitor;
  if (!cap) return false;
  if (typeof cap.isNativePlatform === 'function') {
    try { return !!cap.isNativePlatform(); } catch { return false; }
  }
  return cap.platform === 'ios' || cap.platform === 'android';
};

/**
 * True iff the page is being viewed in mobile Safari on an iPhone, AND
 * the user is NOT already inside the Capacitor iOS shell.
 *
 * Used to gate the "Get the iOS app" secondary CTA on ClubDetail and
 * RunPreview. We deliberately scope to iPhone (not iPad) because the
 * Smart App Banner and our App Store listing target iPhone.
 */
export const isIPhoneWebSafari = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (isCapacitorNative()) return false;
  const ua = navigator.userAgent || '';
  // iPhone UA, exclude in-app browsers that re-route to native flows
  // unreliably (Chrome iOS = CriOS, Firefox iOS = FxiOS, Edge iOS = EdgiOS).
  // Those still get the desktop fallback or nothing — they don't get the
  // Apple Smart Banner anyway.
  const isIPhone = /iPhone/.test(ua);
  const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS/.test(ua);
  return isIPhone && !isOtherIOSBrowser;
};

/**
 * True iff the page is on a desktop browser (no mobile UA tokens) and
 * NOT inside Capacitor. Used to gate the soft "open this link on your
 * phone" hint on ClubDetail and RunPreview.
 */
export const isDesktopWeb = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (isCapacitorNative()) return false;
  const ua = navigator.userAgent || '';
  const isMobileUA = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
  return !isMobileUA;
};
